/**
 * Custom Opt-in report (task 035-06) — "Custom Optin Page": one row per page
 * with her column set, a row expander listing leads per originating site ID,
 * per-row `All` / `Unique` exports, links to the editor and to the live page.
 *
 * Revenue follows the send mode (README decision 7); this screen never
 * recomputes it from counts — it renders `revenue` / `billableUniqueLeads`
 * exactly as the pinned contract returns them. `CAMPAIGN_LEADS_EXPORT_AVAILABLE`
 * (task 07) and `COOP_UNIQUE_EXPORT_AVAILABLE` (task 08) gate the export
 * buttons per row (`custom-optin-stats-api.ts`) — both are `false` until
 * their tail tasks land.
 *
 * *Page views* are the pages the platform served (epic 079): a range with no
 * page-view data renders "—" in both columns and the footer, and a note above
 * the table says from which day they count.
 *
 * Talks to the api through the screen-local `custom-optin-stats-api.ts`
 * (A2 (b)) and the shared `api` client for the advertiser / campaign
 * dropdowns only (007's precedent).
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AdvertiserResponse, CampaignStatus } from '@leads-platform/shared';
import { campaignStatusEnum } from '@leads-platform/shared';
import { api, type CampaignDetailResponse } from '../../lib/api';
import { pageUrl } from '../../lib/campaign-styles';
import { fmtCount, fmtDate, fmtMoney } from '../../lib/format';
import { Card } from '../../components/ui/card';
import { Select } from '../../components/ui/select';
import { Typeahead } from '../../components/typeahead';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  CAMPAIGN_LEADS_EXPORT_AVAILABLE,
  COOP_UNIQUE_EXPORT_AVAILABLE,
  customOptinLeadsExportUrl,
  fetchCustomOptinStats,
  type CustomOptinLeadsVariant,
  type CustomOptinRow,
  type CustomOptinStatsFilters,
  type CustomOptinStatsResponse,
} from './custom-optin-stats-api';
import { ReportShell } from './report-shell';
import {
  RangeFilters,
  ReportSearch,
  SearchNote,
  TestLeadsToggle,
  matchesSearch,
  useReportRange,
  useSharedReportFilters,
} from './report-filters';
import {
  cell,
  EmptyRow,
  ErrorBanner,
  LoadingRow,
  SortableHeader,
  UNIQUE_TOTAL_TITLE,
  useClientSort,
} from './report-table';

/** Epic 079 D8 — verbatim, the *Page views* / *Unique page views* column help. */
const PAGE_VIEWS_HELP =
  'Pages shown to visitors, recorded when the platform serves the page: one per visitor walk, page and day (a reload or Back adds none); unique by IP address; not affected by the test-leads toggle';

/** Epic 079 D8 — verbatim, the *CP clicks* / *CP unique clicks* column help. */
const CLICKS_HELP =
  'Accepts recorded by the platform; unique by IP address; not affected by the test-leads toggle';

const NOT_RECORDED_TITLE = 'Not recorded for these dates';

/**
 * A page-view cell (epic 079 D4): `null` means the range has no page-view
 * data at all and reads "—", never an empty zero; a number renders as every
 * other count does, keeping the caller's `title` (the footer's unique help).
 */
function ViewCell({ value, title }: { value: number | null; title?: string }) {
  return (
    <TableCell
      className="text-right tabular-nums"
      title={value === null ? NOT_RECORDED_TITLE : title}
    >
      {value === null ? '—' : cell(value)}
    </TableCell>
  );
}

/**
 * The one-line coverage note above the table (epic 079 D4): none for `full`;
 * `partial` names the day recording began, `none` says the range predates it.
 */
function pageViewsNote(
  coverage: CustomOptinStatsResponse['pageViewsCoverage'],
  since: string | null
): string | null {
  switch (coverage) {
    case 'full':
      return null;
    case 'partial':
      return `Page views count pages shown to visitors from ${fmtDate(since)}. Earlier days in this range have none, and ${fmtDate(since)} itself is partial.`;
    case 'none':
      return since === null
        ? "Page views aren't recorded yet."
        : `Page views aren't recorded for these dates. Counting started ${fmtDate(since)}.`;
  }
}

/** README §Decisions pre-resolved (Custom Opt-in) — the two `Revenue` literals, verbatim, nothing appended. */
const REVENUE_HELP: Record<'click_out' | 'lead_post', string> = {
  click_out:
    'clicks × CPC — counts every accept request the platform received; not filtered by the test-leads toggle',
  lead_post:
    "billable unique × CPL, keyed on the send's completion date (leads are keyed on capture date); may be lower than Unique email leads when captures were suppressed, capped, or sent after the range",
};

function revenueHelp(sendMode: CustomOptinRow['sendMode']): string | undefined {
  if (sendMode === 'click_out' || sendMode === 'lead_post') return REVENUE_HELP[sendMode];
  return undefined;
}

/** Task 06 §Scope 2: `Cost` help is `CPC` for `click_out`, `CPL` otherwise. */
function costHelp(sendMode: CustomOptinRow['sendMode']): string {
  return sendMode === 'click_out' ? 'CPC' : 'CPL';
}

const LIMIT_TYPE_LABELS: Record<'total_clicks' | 'unique_leads' | 'unique_clicks', string> = {
  total_clicks: 'Total clicks',
  unique_leads: 'Unique leads',
  unique_clicks: 'Unique clicks',
};

/** README §Decisions pre-resolved (Custom Opt-in) label map — never the raw enum value. */
function limitTypeLabel(limitType: CustomOptinRow['limitType']): string {
  return limitType === null ? '' : LIMIT_TYPE_LABELS[limitType];
}

/** `500 / day 50` with the cap set (exact spacing), `500` without one, empty with no limit. */
function limitCell(limitValue: number | null, dailyCap: number | null): string {
  if (limitValue === null) return '';
  if (dailyCap === null) return fmtCount(limitValue);
  return `${fmtCount(limitValue)} / day ${fmtCount(dailyCap)}`;
}

/** `''` (no site) renders as the screen's `(none)` label — never the DTO value (pinned contract). */
function siteIdLabel(siteId: string): string {
  return siteId === '' ? '(none)' : siteId;
}

type SortKey =
  | 'advertiser'
  | 'pageTitle'
  | 'start'
  | 'end'
  | 'cost'
  | 'limitType'
  | 'limit'
  | 'pageViews'
  | 'uniquePageViews'
  | 'clicks'
  | 'uniqueClicks'
  | 'leads'
  | 'uniqueLeads'
  | 'revenue'
  | 'status';

function rowAccessor(row: CustomOptinRow, key: SortKey): string | number | null {
  switch (key) {
    case 'advertiser':
      return row.advertiserName;
    case 'pageTitle':
      return row.pageTitle ?? row.campaignName;
    case 'start':
      return row.startDate;
    case 'end':
      return row.endDate;
    case 'cost':
      return row.pricePerLead;
    case 'limitType':
      return row.limitType;
    case 'limit':
      return row.limitValue;
    case 'pageViews':
      return row.pageViews;
    case 'uniquePageViews':
      return row.uniquePageViews;
    case 'clicks':
      return row.clicks;
    case 'uniqueClicks':
      return row.uniqueClicks;
    case 'leads':
      return row.leads;
    case 'uniqueLeads':
      return row.uniqueLeads;
    case 'revenue':
      return row.revenue;
    case 'status':
      return row.status;
  }
}

/** A row is routed to task 07's endpoint (`campaign-leads.csv`) when it is not a `lead_post` row. */
function exportVariantsFor(row: CustomOptinRow): CustomOptinLeadsVariant[] {
  if (row.sendMode === 'lead_post') {
    return COOP_UNIQUE_EXPORT_AVAILABLE ? ['all', 'unique'] : ['all'];
  }
  return CAMPAIGN_LEADS_EXPORT_AVAILABLE ? ['all', 'unique'] : [];
}

const EXPORT_LABELS: Record<CustomOptinLeadsVariant, string> = { all: 'All', unique: 'Unique' };

function ExportButtons({
  row,
  onExport,
}: {
  row: CustomOptinRow;
  onExport: (row: CustomOptinRow, variant: CustomOptinLeadsVariant) => void;
}) {
  const variants = exportVariantsFor(row);
  if (variants.length === 0) return null;
  return (
    <div className="flex justify-end gap-1">
      {variants.map((variant) => (
        <button
          key={variant}
          type="button"
          data-testid={`custom-optin-export-${variant}-${row.campaignId}`}
          onClick={() => onExport(row, variant)}
          className="rounded-md px-1.5 py-0.5 text-xs font-medium text-accent hover:underline"
        >
          {EXPORT_LABELS[variant]}
        </button>
      ))}
    </div>
  );
}

const COLSPAN = 17;

const STATUS_BY_VALUE: ReadonlyMap<string, CampaignStatus> = new Map(
  campaignStatusEnum.options.map((status) => [status, status])
);

export function CustomOptinStats() {
  const navigate = useNavigate();
  const range = useReportRange();
  const { from, to } = range;
  const [advertiserId, setAdvertiserId] = useState('');
  const [campaignId, setCampaignId] = useState('');
  // Status and test leads are shared across the style tabs (URL-backed, 087-01).
  const { campaignStatus, setCampaignStatus, includeTest, setIncludeTest } =
    useSharedReportFilters();
  const [search, setSearch] = useState('');
  const [advertisers, setAdvertisers] = useState<AdvertiserResponse[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignDetailResponse[]>([]);
  const [data, setData] = useState<CustomOptinStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(new Set());

  // Dropdown loaders — the shared typed client (best-effort: an empty
  // dropdown list degrades the filter, not the report).
  useEffect(() => {
    api
      .getAdvertisers()
      .then(setAdvertisers)
      .catch(() => {
        // Leave the previous (empty) list on error.
      });
    api
      .getCampaigns()
      .then(setCampaigns)
      .catch(() => {
        // Leave the previous (empty) list on error.
      });
  }, []);

  const filters: CustomOptinStatsFilters = {
    from,
    to,
    includeTest,
    advertiserId,
    campaignId,
    campaignStatus,
  };

  // Fetch on every filter change; the stale response of a superseded filter
  // set is dropped rather than rendered (co-op-counts.tsx's idiom).
  useEffect(() => {
    if (!from || !to) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchCustomOptinStats({ from, to, includeTest, advertiserId, campaignId, campaignStatus })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Request failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [from, to, includeTest, advertiserId, campaignId, campaignStatus]);

  const campaignOptions = useMemo(
    () =>
      campaigns.filter((c) => {
        if (advertiserId && c.advertiserId !== advertiserId) return false;
        return c.campaignStyle === 'custom_optin';
      }),
    [campaigns, advertiserId]
  );

  // The search narrows the rows only — the footer keeps the server's totals (087-01 D5).
  const searchActive = search.trim() !== '';
  const searchedRows = useMemo(
    () =>
      (data?.rows ?? []).filter((row) =>
        matchesSearch(
          search,
          row.advertiserName,
          row.campaignName,
          row.clientName,
          row.pageTitle,
          row.pageSlug
        )
      ),
    [data, search]
  );

  const { sortedRows, sort, toggleSort } = useClientSort<CustomOptinRow, SortKey>(
    searchedRows,
    rowAccessor
  );

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleExport = (row: CustomOptinRow, variant: CustomOptinLeadsVariant) => {
    window.open(customOptinLeadsExportUrl(row, filters, variant));
  };

  const note = data === null ? null : pageViewsNote(data.pageViewsCoverage, data.pageViewsSince);

  return (
    <ReportShell
      title="Custom Opt-in"
      desc="Funnel per page — pages shown and clicks recorded by the platform (unique by IP), leads, revenue by send mode. Test leads hidden by default."
    >
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3">
          <RangeFilters
            preset={range.preset}
            from={range.from}
            to={range.to}
            applyPreset={range.applyPreset}
            setRange={range.setRange}
            testIdPrefix="custom-optin"
          />

          <Typeahead<AdvertiserResponse>
            aria-label="Advertiser"
            options={advertisers}
            value={advertiserId || null}
            onChange={(v) => {
              setAdvertiserId(v ?? '');
              setCampaignId(''); // a campaign belongs to one advertiser — drop a now-hidden pick
            }}
            getId={(a) => a.id}
            getLabel={(a) => a.name}
            allOption={{ label: 'All advertisers' }}
            className="min-w-[170px]"
          />

          <Select
            aria-label="Campaign"
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
            className="h-9 min-w-[180px]"
          >
            <option value="">All campaigns</option>
            {campaignOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>

          <Select
            aria-label="Status"
            value={campaignStatus}
            onChange={(e) => setCampaignStatus(STATUS_BY_VALUE.get(e.target.value) ?? '')}
            className="h-9 min-w-[140px]"
          >
            <option value="">All statuses</option>
            {campaignStatusEnum.options.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </Select>

          <TestLeadsToggle checked={includeTest} onChange={setIncludeTest} />

          <ReportSearch
            value={search}
            onChange={setSearch}
            placeholder="Search advertiser, campaign, client or page"
            testId="custom-optin-search"
          />

          {loading && <span className="text-xs text-muted-foreground">Loading…</span>}
        </div>

        <SearchNote
          active={searchActive}
          shown={sortedRows.length}
          total={data?.rows.length ?? 0}
          testId="custom-optin-search-note"
        />

        {note !== null && (
          <p
            data-testid="page-views-note"
            className="border-b border-line px-4 py-2 text-xs text-muted-foreground"
          >
            {note}
          </p>
        )}

        {error && <ErrorBanner message={error} />}

        <div className="overflow-x-auto">
          <Table data-testid="custom-optin-table">
            <TableHeader>
              <TableRow>
                <TableHead aria-hidden="true" />
                <SortableHeader
                  label="Advertiser"
                  sortKey="advertiser"
                  sort={sort}
                  onToggle={toggleSort}
                />
                <SortableHeader
                  label="Page title"
                  sortKey="pageTitle"
                  sort={sort}
                  onToggle={toggleSort}
                />
                <SortableHeader label="Start" sortKey="start" sort={sort} onToggle={toggleSort} />
                <SortableHeader label="End" sortKey="end" sort={sort} onToggle={toggleSort} />
                <SortableHeader label="Cost" sortKey="cost" sort={sort} onToggle={toggleSort} />
                <SortableHeader
                  label="Limit type"
                  sortKey="limitType"
                  sort={sort}
                  onToggle={toggleSort}
                />
                <SortableHeader label="Limit" sortKey="limit" sort={sort} onToggle={toggleSort} />
                <SortableHeader
                  label="Page views"
                  sortKey="pageViews"
                  sort={sort}
                  onToggle={toggleSort}
                  title={PAGE_VIEWS_HELP}
                />
                <SortableHeader
                  label="Unique page views"
                  sortKey="uniquePageViews"
                  sort={sort}
                  onToggle={toggleSort}
                  title={PAGE_VIEWS_HELP}
                />
                <SortableHeader
                  label="CP clicks"
                  sortKey="clicks"
                  sort={sort}
                  onToggle={toggleSort}
                  title={CLICKS_HELP}
                />
                <SortableHeader
                  label="CP unique clicks"
                  sortKey="uniqueClicks"
                  sort={sort}
                  onToggle={toggleSort}
                  title={CLICKS_HELP}
                />
                <SortableHeader
                  label="Total email leads"
                  sortKey="leads"
                  sort={sort}
                  onToggle={toggleSort}
                />
                <SortableHeader
                  label="Unique email leads"
                  sortKey="uniqueLeads"
                  sort={sort}
                  onToggle={toggleSort}
                />
                <SortableHeader
                  label="Revenue"
                  sortKey="revenue"
                  sort={sort}
                  onToggle={toggleSort}
                />
                <SortableHeader label="Status" sortKey="status" sort={sort} onToggle={toggleSort} />
                <TableHead className="text-right">Exports</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data === null ? (
                <LoadingRow colSpan={COLSPAN} />
              ) : sortedRows.length === 0 ? (
                <EmptyRow
                  colSpan={COLSPAN}
                  message={
                    data.rows.length > 0 && searchActive
                      ? `No rows match "${search.trim()}".`
                      : 'No pages in this range.'
                  }
                />
              ) : (
                sortedRows.map((row) => {
                  const expanded = expandedIds.has(row.campaignId);
                  const live = pageUrl(row.pageSlug);
                  return (
                    <Fragment key={row.campaignId}>
                      <TableRow data-testid={`custom-optin-row-${row.campaignId}`}>
                        <TableCell>
                          <button
                            type="button"
                            aria-expanded={expanded}
                            aria-label={`Toggle sites for ${row.pageTitle ?? row.campaignName}`}
                            data-testid={`custom-optin-expand-${row.campaignId}`}
                            onClick={() => toggleExpanded(row.campaignId)}
                            className="text-faint hover:text-ink"
                          >
                            {expanded ? '▾' : '▸'}
                          </button>
                        </TableCell>
                        <TableCell>
                          {row.advertiserName}
                          {row.clientName ? ` – ${row.clientName}` : ''}
                        </TableCell>
                        <TableCell>
                          <button
                            type="button"
                            onClick={() => navigate(`/campaigns/${row.campaignId}`)}
                            className="text-accent hover:underline"
                          >
                            {row.pageTitle ?? row.campaignName}
                          </button>
                          {live && (
                            <a
                              href={live}
                              target="_blank"
                              rel="noreferrer"
                              aria-label="View live page"
                              className="ml-1 text-faint hover:text-ink"
                            >
                              ↗
                            </a>
                          )}
                        </TableCell>
                        <TableCell>{fmtDate(row.startDate)}</TableCell>
                        <TableCell>{fmtDate(row.endDate)}</TableCell>
                        <TableCell
                          className="text-right tabular-nums"
                          title={costHelp(row.sendMode)}
                        >
                          {fmtMoney(row.pricePerLead)}
                        </TableCell>
                        <TableCell>{limitTypeLabel(row.limitType)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {limitCell(row.limitValue, row.dailyCap)}
                        </TableCell>
                        <ViewCell value={row.pageViews} />
                        <ViewCell value={row.uniquePageViews} />
                        <TableCell className="text-right tabular-nums">
                          {cell(row.clicks)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cell(row.uniqueClicks)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{cell(row.leads)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cell(row.uniqueLeads)}
                        </TableCell>
                        <TableCell
                          className="text-right tabular-nums"
                          title={revenueHelp(row.sendMode)}
                        >
                          {fmtMoney(row.revenue)}
                        </TableCell>
                        <TableCell>{row.status}</TableCell>
                        <TableCell className="text-right">
                          <ExportButtons row={row} onExport={handleExport} />
                        </TableCell>
                      </TableRow>
                      {expanded && (
                        <TableRow data-testid={`custom-optin-sites-${row.campaignId}`}>
                          <TableCell colSpan={COLSPAN} className="bg-paper/50 p-0">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Site ID</TableHead>
                                  <TableHead className="text-right">Leads</TableHead>
                                  <TableHead className="text-right">Unique</TableHead>
                                  <TableHead className="text-right">Rejected</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {row.sites.map((site) => (
                                  <TableRow key={site.siteId || '(none)'}>
                                    <TableCell>{siteIdLabel(site.siteId)}</TableCell>
                                    <TableCell className="text-right tabular-nums">
                                      {cell(site.leads)}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                      {cell(site.uniqueLeads)}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                      {cell(site.rejected)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })
              )}
            </TableBody>
            {data !== null && data.rows.length > 0 && (
              <TableFooter>
                <TableRow data-testid="custom-optin-totals-row">
                  <TableCell>Total</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <ViewCell value={data.totals.pageViews} />
                  <ViewCell value={data.totals.uniquePageViews} title={UNIQUE_TOTAL_TITLE} />
                  <TableCell className="text-right tabular-nums">
                    {cell(data.totals.clicks)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                    {cell(data.totals.uniqueClicks)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {cell(data.totals.leads)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                    {cell(data.totals.uniqueLeads)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmtMoney(data.totals.revenue)}
                  </TableCell>
                  <TableCell />
                  <TableCell />
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </div>
      </Card>
    </ReportShell>
  );
}
